import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, TrendingUp, CheckCircle, Code, Heart, Clock } from 'lucide-react';

export const MedicalClinicPortfolio: React.FC = () => {
  const technologies = [
    'WordPress', 'PHP', 'MySQL', 'AJAX', 'HIPAA Compliance', 'SSL'
  ];

  const features = [
    'Online Appointment Booking',
    'Patient Portal',
    'HIPAA Compliant Forms',
    'Insurance Verification',
    'Telemedicine Integration',
    'Medical Records Access',
    'Prescription Refills',
    'Mobile Responsive Design'
  ];

  const results = [
    { metric: 'Online Bookings', value: '85%', description: 'Of all appointments now booked online' },
    { metric: 'Patient Satisfaction', value: '96%', description: 'Positive feedback on new system' },
    { metric: 'Admin Time Saved', value: '40%', description: 'Reduction in administrative tasks' },
    { metric: 'No-Show Rate', value: '15%', description: 'Decrease due to automated reminders' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/portfolio" className="inline-flex items-center text-primary-100 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Portfolio
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <span className="px-3 py-1 bg-accent-500 text-white text-sm font-medium rounded-full">
                  Medical Clinic
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Wellness Family Medical
              </h1>
              <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                A comprehensive medical practice website with HIPAA-compliant patient portal, 
                online appointment booking, and telemedicine integration that streamlined operations 
                and improved patient experience.
              </p>
            </div>
            
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="Medical Clinic Website"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="mb-6">
                  Wellness Family Medical needed to modernize their patient experience while maintaining 
                  strict HIPAA compliance. Their existing system was outdated, causing administrative 
                  bottlenecks and patient frustration with appointment scheduling.
                </p>
                <p className="mb-6">
                  We developed a comprehensive WordPress-based solution with custom PHP development for 
                  secure patient portals, online appointment booking, and telemedicine capabilities. 
                  The system integrates with their existing practice management software while ensuring 
                  full HIPAA compliance.
                </p>
                <p>
                  The implementation resulted in 85% of appointments being booked online, 96% patient 
                  satisfaction, and a 40% reduction in administrative workload.
                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-6 w-6 text-primary-600 mr-2" />
                  Project Timeline
                </h3>
                <div className="space-y-3 text-gray-600">
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="font-medium">8 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Launch Date:</span>
                    <span className="font-medium">February 2024</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Team Size:</span>
                    <span className="font-medium">4 specialists</span>
                  </div>
                </div>
              </div>
              
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Code className="h-6 w-6 text-primary-600 mr-2" />
                  Technologies Used
                </h3>
                <div className="flex flex-wrap gap-2">
                  {technologies.map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features Delivered</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              HIPAA-compliant features designed to enhance patient care while streamlining clinic operations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="card p-6 text-center">
                <CheckCircle className="h-8 w-8 text-accent-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">{feature}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results & Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Results & Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Significant improvements in patient satisfaction and operational efficiency.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary-600 mb-2">{result.value}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{result.metric}</div>
                <div className="text-gray-600">{result.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Challenges & Solutions</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Heart className="h-8 w-8 text-red-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: HIPAA Compliance</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Ensuring all patient data handling, storage, and transmission met strict HIPAA requirements 
                while maintaining user-friendly functionality.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Implemented end-to-end encryption, secure authentication, 
                audit logging, and regular security assessments to ensure full HIPAA compliance.
              </p>
            </div>
            
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Clock className="h-8 w-8 text-blue-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: System Integration</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Integrating the new patient portal with existing practice management software and 
                electronic health records systems.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Developed custom APIs and middleware to seamlessly connect 
                systems while maintaining data integrity and real-time synchronization.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonial */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl font-medium mb-8">
            "The new patient portal has revolutionized our practice. Our patients love the convenience, 
            and our staff has more time to focus on patient care instead of administrative tasks."
          </blockquote>
          <div className="flex items-center justify-center">
            <img
              src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop"
              alt="Dr. Emily Rodriguez"
              className="w-16 h-16 rounded-full mr-4"
            />
            <div className="text-left">
              <div className="font-semibold">Dr. Emily Rodriguez</div>
              <div className="text-primary-200">Medical Director, Wellness Family Medical</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Need a Healthcare Website Solution?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how we can create a HIPAA-compliant solution that enhances patient care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="btn-primary text-lg px-8 py-4">
              Start Your Project
            </Link>
            <Link to="/portfolio" className="btn-outline text-lg px-8 py-4">
              View More Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};